import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Trophy, Clock, Users, Award } from 'lucide-react';

interface ContestRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ContestRulesModal({ isOpen, onClose }: ContestRulesModalProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader className="pe-12 text-start">
          <DialogTitle className="flex items-center gap-2 text-xl sm:text-2xl text-start">
            <Trophy className="w-6 h-6 text-yellow-500 shrink-0" />
            {t('modals.founding100.info.dialogTitle')}
          </DialogTitle>
          <DialogDescription className="text-start">
            {t('modals.founding100.info.dialogSubtitle')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4 text-start">
          {/* Overview */}
          <div className="space-y-2">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Award className="w-5 h-5 text-pink-500 shrink-0" />
              {t('modals.founding100.info.overviewTitle')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-white/70">
              {t('modals.founding100.info.overviewBody')}
            </p>
            <div className="rounded-lg border border-pink-200/60 dark:border-pink-500/30 bg-pink-50/50 dark:bg-pink-900/10 p-3 space-y-2">
              <p className="text-sm font-medium text-pink-700 dark:text-pink-300">
                {t('modals.founding100.info.fairContestTitle')}
              </p>
              <p className="text-sm text-gray-600 dark:text-white/70">
                {t('modals.founding100.info.fairContestBody')}
              </p>
            </div>
            <p className="text-sm font-medium text-gray-700 dark:text-white/80">
              {t('modals.founding100.info.approvedNotWinner')}
            </p>
          </div>

          {/* How It Works */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-500 shrink-0" />
              {t('modals.founding100.info.howItWorksTitle')}
            </h3>
            <div className="space-y-3 text-sm text-gray-600 dark:text-white/70">
              <div className="flex gap-3 items-start">
                <div className="shrink-0 w-6 h-6 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center text-pink-600 dark:text-pink-400 font-bold text-xs">
                  1
                </div>
                <div className="min-w-0">
                  <strong className="text-gray-900 dark:text-white block">
                    {t('modals.founding100.info.step1Title')}
                  </strong>
                  <p className="mt-1">{t('modals.founding100.info.step1Body')}</p>
                </div>
              </div>

              <div className="flex gap-3 items-start">
                <div className="shrink-0 w-6 h-6 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center text-pink-600 dark:text-pink-400 font-bold text-xs">
                  2
                </div>
                <div className="min-w-0">
                  <strong className="text-gray-900 dark:text-white block">
                    {t('modals.founding100.info.step2Title')}
                  </strong>
                  <p className="mt-1">{t('modals.founding100.info.step2Body')}</p>
                </div>
              </div>

              <div className="flex gap-3 items-start">
                <div className="shrink-0 w-6 h-6 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center text-pink-600 dark:text-pink-400 font-bold text-xs">
                  3
                </div>
                <div className="min-w-0">
                  <strong className="text-gray-900 dark:text-white block">
                    {t('modals.founding100.info.step3Title')}
                  </strong>
                  <p className="mt-1">{t('modals.founding100.info.step3Body')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Ranking Criteria */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-500 shrink-0" />
              {t('modals.founding100.info.rankingCriteriaTitle')}
            </h3>
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-white/70">
                {t('modals.founding100.info.rankingCriteriaBody')}
              </p>
            </div>
          </div>

          {/* Prize */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-4 border-2 border-yellow-200 dark:border-yellow-800">
            <div className="flex items-start gap-3">
              <Trophy className="w-6 h-6 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                  {t('modals.founding100.info.prizeTitle')}
                </h4>
                <p className="text-sm text-gray-700 dark:text-white/80">
                  {t('modals.founding100.info.prizeBody')}
                </p>
              </div>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={onClose}
          >
            {t('modals.founding100.info.close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
